import {
	Brain,
	Volume2,
	ChevronDown,
	CheckCircle,
} from "lucide-react";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "../../../components/ui/dialog";

export const SmartAudioDialog = ({
	isOpen,
	onClose,
	settings,
	onSettingsChange
}) => {
	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="sm:max-w-[500px] max-h-[90vh] bg-grayBase/95 backdrop-blur-xl border border-emeraldBase/30 overflow-hidden flex flex-col">
				<DialogHeader className="flex-shrink-0">
					<DialogTitle className="flex items-center gap-3 text-white">
						<Brain className="h-5 w-5 text-emeraldLight" />
						Configurações do Áudio Inteligente
					</DialogTitle>
				</DialogHeader>

				<div className="flex-1 overflow-y-auto">
					<div className="space-y-6 py-4">
					<div className="p-4 bg-emeraldBase/10 border border-emeraldBase/30 rounded-lg">
						<h4 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
							<span className="text-emeraldMedium">🧠</span>
							Como Funciona o Áudio Inteligente
						</h4>
						<div className="space-y-2 text-xs text-gray-300">
							<div className="flex items-start gap-2">
								<span className="text-emeraldMedium mt-0.5">1️⃣</span>
								<span>A IA analisa a resposta e divide em blocos de texto</span>
							</div>
							<div className="flex items-start gap-2">
								<span className="text-emeraldMedium mt-0.5">2️⃣</span>
								<span>Filtra apenas blocos com o número mínimo de caracteres</span>
							</div>
							<div className="flex items-start gap-2">
								<span className="text-emeraldMedium mt-0.5">3️⃣</span>
								<span>Escolhe <strong>apenas 1 bloco</strong> (o mais adequado) para converter em áudio</span>
							</div>
							<div className="flex items-start gap-2">
								<span className="text-emeraldMedium mt-0.5">4️⃣</span>
								<span>Envia o texto completo + o áudio do bloco selecionado</span>
							</div>
						</div>
					</div>

					<div className="p-4 bg-white/5 backdrop-blur-xl rounded-lg border border-emeraldBase/25">
						<div className="flex items-center gap-3 mb-4">
							<span className="text-xl">📏</span>
							<div>
								<label className="text-sm font-medium text-white/90">
									Mínimo de Caracteres para Áudio
								</label>
								<p className="text-xs text-gray-400">
									Apenas blocos com este número mínimo de caracteres serão elegíveis para áudio
								</p>
							</div>
						</div>

						<div className="space-y-3">
							<div className="flex items-center gap-4">
								<input
									type="range"
									name="TTS_SMART_AUDIO_MIN_CHARS"
									min="20"
									max="200"
									step="10"
									value={settings.TTS_SMART_AUDIO_MIN_CHARS}
									onChange={onSettingsChange}
									className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider-emerald"
								/>
								<span className="text-sm text-white/90 font-medium min-w-[70px] text-center bg-emeraldBase/20 px-3 py-1 rounded-md border border-emeraldBase/40">
									{settings.TTS_SMART_AUDIO_MIN_CHARS} chars
								</span>
							</div>

							<div className="text-xs text-gray-400 space-y-1">
								<div className="flex justify-between">
									<span>• Palavra curta (20-40 chars):</span>
									<span className="text-gray-500">"Olá, como você está?"</span>
								</div>
								<div className="flex justify-between">
									<span>• Frase média (70-100 chars):</span>
									<span className="text-gray-500">"Esta é uma explicação mais detalhada sobre o assunto que você perguntou."</span>
								</div>
								<div className="flex justify-between">
									<span>• Parágrafo (150+ chars):</span>
									<span className="text-gray-500">Textos longos com múltiplas frases...</span>
								</div>
							</div>
						</div>
					</div>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export const ElevenLabsDialog = ({
	isOpen,
	onClose,
	settings,
	onSettingsChange,
	onSave,
	useCustomVoice,
	setUseCustomVoice,
	selectedVoice,
	setSelectedVoice,
	customVoiceId,
	setCustomVoiceId
}) => {
	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="sm:max-w-[500px] max-h-[90vh] bg-grayBase/95 backdrop-blur-xl border border-emeraldBase/30 overflow-hidden flex flex-col">
				<DialogHeader className="flex-shrink-0">
					<DialogTitle className="flex items-center gap-3 text-white">
						<Volume2 className="h-5 w-5 text-emeraldLight" />
						Configurações ElevenLabs
					</DialogTitle>
				</DialogHeader>

				<div className="flex-1 overflow-y-auto">
					<div className="space-y-6 py-4">
					<div className="space-y-2">
						<label className="text-sm font-medium text-white/90">
							API Key
						</label>
						<input
							type="text"
							name="ELEVENLABS_API_KEY"
							value={settings.ELEVENLABS_API_KEY}
							onChange={onSettingsChange}
							placeholder="Cole sua API key do ElevenLabs aqui"
							className="w-full rounded-lg border border-emeraldBase/25 bg-white/5 backdrop-blur-xl px-4 py-3 text-white focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 shadow-lg placeholder-gray-400"
						/>
						<p className="text-xs text-gray-400">
							Obtenha sua API key em <a href="https://elevenlabs.io" target="_blank" rel="noopener noreferrer" className="text-emeraldMedium hover:text-emeraldLight">elevenlabs.io</a>
						</p>
					</div>

					{/* Status da Voz Atual */}
					<div className="p-4 bg-statusInfo/10 border border-statusInfo/30 rounded-lg">
						<h4 className="text-statusInfo font-semibold text-sm mb-2 flex items-center gap-2">
							<span>🎵</span>
							Voz Atual
						</h4>
						<div className="space-y-1 text-xs text-statusInfo/80">
							<p><strong>Voice ID:</strong> {settings.ELEVENLABS_VOICE_ID || "pNInz6obpgDQGcFmaJgB"}</p>
							<p><strong>Tipo:</strong> {useCustomVoice ? "Voz Personalizada" : "Voz Padrão"}</p>
							{!useCustomVoice && (
								<p><strong>Nome:</strong> {
									selectedVoice === "pNInz6obpgDQGcFmaJgB" ? "Adam (Masculino, Americano)" :
									selectedVoice === "ErXwobaYiN019PkySvjV" ? "Antoni (Masculino, Americano)" :
									selectedVoice === "EXAVITQu4vr4xnSDxMaL" ? "Bella (Feminino, Americano)" :
									selectedVoice === "MF3mGyEYCl7XYWbV9V6O" ? "Elli (Feminino, Americano)" :
									selectedVoice === "TxGEqnHWrfWFTfGW9XjX" ? "Josh (Masculino, Americano)" :
									selectedVoice === "AZnzlk1XvdvUeBnXmlld" ? "Rachel (Feminino, Americano)" :
									selectedVoice === "29vD33N1CtxCmqQRPOHJ" ? "Drew (Masculino, Americano)" :
									selectedVoice === "2EiwWnXFnvU5JabPnv8n" ? "Clyde (Masculino, Americano)" :
									selectedVoice === "CYw3kZ02Hs0563khs1Fj" ? "Dave (Masculino, Britânico)" :
									"Voz desconhecida"
								}</p>
							)}
						</div>
					</div>

					<div className="flex items-center justify-between p-4 bg-white/5 backdrop-blur-xl rounded-lg border border-emeraldBase/25">
						<div>
							<label className="text-sm font-medium text-white/90">
								Voz personalizada
							</label>
							<p className="text-xs text-gray-400">
								Use um Voice ID personalizado em vez das vozes padrão
							</p>
						</div>
						<label className="relative inline-flex items-center cursor-pointer">
							<input
								type="checkbox"
								checked={useCustomVoice}
								onChange={(e) => {
									setUseCustomVoice(e.target.checked);
									if (e.target.checked) {
										setCustomVoiceId("");
									}
								}}
								className="sr-only peer"
							/>
							<div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emeraldLight/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emeraldDeep"></div>
						</label>
					</div>

					{!useCustomVoice && (
						<div className="space-y-2">
							<label className="text-sm font-medium text-white/90">
								Voz
							</label>
							<div className="relative">
								<select
									value={selectedVoice}
									onChange={(e) => setSelectedVoice(e.target.value)}
									className="w-full rounded-lg border border-emeraldBase/25 bg-white/5 backdrop-blur-xl px-4 py-3 pr-10 text-white focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 shadow-lg appearance-none cursor-pointer"
								>
								<option value="pNInz6obpgDQGcFmaJgB" className="bg-slateCard text-white">Adam (Masculino, Americano)</option>
								<option value="ErXwobaYiN019PkySvjV" className="bg-slateCard text-white">Antoni (Masculino, Americano)</option>
								<option value="EXAVITQu4vr4xnSDxMaL" className="bg-slateCard text-white">Bella (Feminino, Americano)</option>
								<option value="MF3mGyEYCl7XYWbV9V6O" className="bg-slateCard text-white">Elli (Feminino, Americano)</option>
								<option value="TxGEqnHWrfWFTfGW9XjX" className="bg-slateCard text-white">Josh (Masculino, Americano)</option>
								<option value="AZnzlk1XvdvUeBnXmlld" className="bg-slateCard text-white">Rachel (Feminino, Americano)</option>
								<option value="29vD33N1CtxCmqQRPOHJ" className="bg-slateCard text-white">Drew (Masculino, Americano)</option>
								<option value="2EiwWnXFnvU5JabPnv8n" className="bg-slateCard text-white">Clyde (Masculino, Americano)</option>
								<option value="CYw3kZ02Hs0563khs1Fj" className="bg-slateCard text-white">Dave (Masculino, Britânico)</option>
								</select>
								<div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
									<ChevronDown className="h-4 w-4 text-emeraldLight" />
								</div>
							</div>
							<p className="text-xs text-gray-400">
								Vozes padrão do ElevenLabs com alta qualidade
							</p>
						</div>
					)}

					{useCustomVoice && (
						<div className="space-y-2">
							<label className="text-sm font-medium text-white/90">
								Voice ID Personalizado
							</label>
							<input
								type="text"
								value={customVoiceId}
								onChange={(e) => setCustomVoiceId(e.target.value)}
								placeholder="ex: pNInz6obpgDQGcFmaJgB"
								className="w-full rounded-lg border border-emeraldBase/25 bg-white/5 backdrop-blur-xl px-4 py-3 text-white focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 shadow-lg placeholder-gray-400"
							/>
							<p className="text-xs text-gray-400">
								Cole aqui o ID da sua voz personalizada do ElevenLabs
							</p>
						</div>
					)}

					<div className="p-4 bg-statusInfo/10 border border-statusInfo/30 rounded-lg">
						<h4 className="text-statusInfo font-semibold text-sm mb-2 flex items-center gap-2">
							<span>ℹ️</span>
							Sobre o ElevenLabs
						</h4>
						<div className="space-y-2 text-xs text-statusInfo/80">
							<p>• Vozes de alta qualidade com IA avançada</p>
							<p>• Suporte a múltiplos idiomas</p>
							<p>• Quando ativo, substitui OpenAI/Gemini Audio</p>
							<p>• Funciona com todas as opções de Audio (Substituir, Inteligente, etc.)</p>
						</div>
					</div>

					<div className="flex justify-end pt-4 border-t border-emeraldBase/20">
						<button
							type="button"
							onClick={onSave}
							className="px-6 py-2 bg-emeraldDeep hover:bg-emeraldBase text-white rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
						>
							<CheckCircle className="h-4 w-4" />
							Salvar
						</button>
					</div>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
};
